import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Scale, 
  Shield, 
  Clock, 
  CheckCircle, 
  ArrowRight, 
  Brain,
  Users,
  FileText,
  BarChart3,
  Lightbulb,
  Heart,
  BookOpen,
  Zap,
  Award,
  MessageSquare,
  ChevronDown
} from 'lucide-react'
import Navbar from '../../components/landing/Navbar'
import TrialSignupModal from '../../components/landing/TrialSignupModal'
import ConsultationModal from '../../components/landing/ConsultationModal'
import DemoModal from '../../components/landing/DemoModal'
import { useScrollAnimation } from '../../landing-hooks/useScrollAnimation'
import { useCountUp } from '../../landing-hooks/useCountUp'
import { useThemeColors } from '../../hooks/useThemeColors'

// スタイルオブジェクト
const styles = {
  heroSection: {
    position: 'relative' as const,
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #047857 0%, #059669 50%, #14b8a6 100%)',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center'
  },
  blurCircle1: {
    position: 'absolute' as const,
    top: '5rem',
    left: '2.5rem',
    width: '18rem',
    height: '18rem',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    filter: 'blur(48px)'
  },
  blurCircle2: {
    position: 'absolute' as const,
    bottom: '5rem',
    right: '2.5rem',
    width: '24rem',
    height: '24rem',
    backgroundColor: 'rgba(45, 212, 191, 0.2)',
    borderRadius: '50%',
    filter: 'blur(48px)'
  },
  productBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(8px)',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    marginBottom: '1.5rem'
  },
  mainTitle: {
    fontSize: 'clamp(2rem, 5vw, 3.75rem)',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1.5rem',
    lineHeight: 1.2
  },
  subtitle: {
    fontSize: '1.25rem',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: '2rem'
  },
  keywordContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '1rem',
    marginBottom: '2rem'
  },
  keyword: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'rgba(255, 255, 255, 0.8)'
  },
  ctaContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem'
  },
  primaryButton: {
    backgroundColor: 'white',
    color: '#047857',
    fontWeight: 'bold',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    border: 'none',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s'
  },
  secondaryButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(8px)',
    color: 'white',
    fontWeight: '600',
    padding: '1rem 2rem',
    borderRadius: '0.5rem',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s'
  }
}

const EducationLP = () => {
  const navigate = useNavigate()
  const colors = useThemeColors()
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false)
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false)
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  
  // テーマカラーを使用したスタイル
  const themedStyles = {
    heroSection: {
      position: 'relative' as const,
      minHeight: '100vh',
      background: colors.heroGradient,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center'
    },
    primaryButton: {
      backgroundColor: colors.surface,
      color: colors.primary,
      fontWeight: 'bold',
      padding: '1rem 2rem',
      borderRadius: '0.5rem',
      border: 'none',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer',
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s'
    },
    secondaryButton: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(8px)',
      color: colors.textInverse,
      fontWeight: '600',
      padding: '1rem 2rem',
      borderRadius: '0.5rem',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s'
    }
  }

  // スクロールアニメーション用のフック
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation()
  const { ref: trustedRef, isVisible: trustedVisible } = useScrollAnimation()
  const { ref: problemRef, isVisible: problemVisible } = useScrollAnimation()
  const { ref: solutionRef, isVisible: solutionVisible } = useScrollAnimation()
  const { ref: featuresRef, isVisible: featuresVisible } = useScrollAnimation()
  const { ref: screenshotsRef, isVisible: screenshotsVisible } = useScrollAnimation()

  // カウントアップアニメーション
  const timeReduction = useCountUp(70, trustedVisible)
  const satisfactionRate = useCountUp(90, trustedVisible)

  useEffect(() => {
    document.documentElement.classList.add('font-smb')
    return () => document.documentElement.classList.remove('font-smb')
  }, [])

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white' }}>
      <Navbar />

      {/* Hero Section */}
      <section style={themedStyles.heroSection}>
        {/* 装飾的な背景要素 */}
        <div style={{ position: 'absolute', inset: 0 }}>
          <div style={styles.blurCircle1} />
          <div style={styles.blurCircle2} />
        </div>

        <div className="container-hero" style={{ position: 'relative', zIndex: 10 }}>
          <div style={{ display: 'grid', gap: '2rem', alignItems: 'center', maxWidth: '80rem', margin: '0 auto' }}>
            <motion.div
              ref={heroRef}
              initial={{ opacity: 0, y: 30 }}
              animate={heroVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              {/* プロダクトバッジ */}
              <div style={styles.productBadge}>
                <Scale className="w-5 h-5" />
                <span style={{ fontWeight: '500' }}>Evaloid - AI評価支援システム</span>
              </div>

              {/* メインコピー */}
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: '-200px',
                    left: '-250px',
                    width: '400px',
                    height: '400px',
                    zIndex: -10
                  }}
                >
                  <img 
                    src="/images/stamp.png" 
                    alt="スタンプ" 
                    style={{
                      width: '100%',
                      height: '100%',
                      opacity: 0.3,
                      objectFit: 'contain',
                      filter: 'brightness(0) saturate(100%) invert(1)'
                    }}
                  />
                </div>
                <h1 style={styles.mainTitle}>
                  もう、評価で悩まない。
                  <br />
                  <span style={{ color: '#a7f3d0' }}>AIが支える、あなたの公正な評価</span>
                </h1>
              </div>

              {/* サブコピー */}
              <p style={styles.subtitle}>
                複雑な評価業務を、シンプルで透明性の高いプロセスへ。
                <br />
                AIはあなたの評価判断を支援する、強力な伴走者です。
              </p>

              {/* 3つのキーワード */}
              <div style={styles.keywordContainer}>
                <div style={{
                  ...styles.keyword,
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 0, 0, 0.3)'
                }}>
                  <Shield className="w-5 h-5" />
                  <span>公正性の確保</span>
                </div>
                <div style={{
                  ...styles.keyword,
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 0, 0, 0.3)'
                }}>
                  <Zap className="w-5 h-5" />
                  <span>業務効率化</span>
                </div>
                <div style={{
                  ...styles.keyword,
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 0, 0, 0.3)'
                }}>
                  <MessageSquare className="w-5 h-5" />
                  <span>説明責任の実現</span>
                </div>
              </div>

              {/* CTA ボタン */}
              <div style={styles.ctaContainer}>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={() => navigate('/auth')}
                    style={themedStyles.primaryButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    まずは無料で試してみる
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* スクロールインジケーター */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            style={{
              position: 'absolute',
              bottom: '-10rem',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          >
            <ChevronDown style={{ width: '2rem', height: '2rem', color: 'rgba(255, 255, 255, 0.6)' }} />
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section - 導入実績がある場合のみ表示 */}
      {false && (
        <section ref={trustedRef} style={{ padding: '4rem 0', backgroundColor: '#f9fafb' }}>
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={trustedVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              style={{ textAlign: 'center' }}
            >
              <p style={{ color: '#666', marginBottom: '2rem', fontWeight: '500' }}>導入実績</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '3rem', alignItems: 'center' }}>
                {[
                  '東京都立〇〇高等学校',
                  '株式会社△△商事 人事部',
                  '□□コンテスト実行委員会',
                  '私立〇〇大学',
                  '××市教育委員会'
                ].map((company, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={trustedVisible ? { opacity: 1 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    style={{ color: '#9ca3af', transition: 'color 0.3s', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#666' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af' }}
                  >
                    <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>{company}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Problem Section */}
      <section ref={problemRef} className="section-padding" style={{ backgroundColor: 'white' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={problemVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              評価業務で、こんな悩みはありませんか？
            </h2>
          </motion.div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: windowWidth >= 768 ? 'repeat(2, 1fr)' : '1fr',
            gap: '2rem' 
          }}>
            {[
              {
                num: '01',
                title: '評価の説明に苦労',
                description: '「なぜこの評価なのか」を説明するのに時間がかかる',
                cost: '保護者対応で年間20時間以上'
              },
              {
                num: '02',
                title: '評価基準のブレ',
                description: '複数の評価対象を一貫した基準で評価するのが困難',
                cost: '公平性への不安'
              },
              {
                num: '03',
                title: '作業時間の負担',
                description: '評価表作成や成績処理に膨大な時間',
                cost: '本来の教育・指導時間が削減'
              },
              {
                num: '04',
                title: '主観評価への不安',
                description: '「私の評価は適切か？」という心理的プレッシャー',
                cost: '評価期間中のストレス'
              }
            ].map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={problemVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '1rem',
                  padding: '2rem',
                  transition: 'box-shadow 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#e5e7eb', marginBottom: '0.5rem' }}>
                      {problem.num}
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
                      {problem.title}
                    </h3>
                    <p style={{ color: '#666', marginBottom: '1rem' }}>{problem.description}</p>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#fee2e2',
                      color: '#991b1b',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.875rem'
                    }}>
                      <ArrowRight style={{ width: '1rem', height: '1rem' }} />
                      {problem.cost}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={problemVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ marginTop: '3rem', textAlign: 'center' }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#d1fae5',
              color: '#065f46',
              padding: '0.75rem 1.5rem',
              borderRadius: '9999px'
            }}>
              <Lightbulb style={{ width: '1.25rem', height: '1.25rem' }} />
              <span style={{ fontWeight: '500' }}>これらの課題を、Evaloidが解決します</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section ref={solutionRef} className="section-padding" style={{ background: 'linear-gradient(to bottom, #f9fafb, white)' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={solutionVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              AIと共に創る、新しい評価のカタチ
            </h2>
            <p style={{ fontSize: '1.25rem', color: '#666' }}>
              Evaloidは評価の主体であるあなたを支援し、より良い評価を実現します
            </p>
          </motion.div>

          {/* 3つの柱 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '4rem' }}>
            {[
              {
                icon: Brain,
                title: 'AIがあなたの評価観点を整理',
                description: '評価方針を入力すると、AIが適切な観点を提案。最終的な選択はあなたが行います。',
                gradient: `linear-gradient(135deg, ${colors.infoLight}, ${colors.secondaryLight})`,
                image: '/images/features/ai-criteria-analysis.png'
              },
              {
                icon: Users,
                title: '視覚的な比較で直感的に評価',
                description: 'ドラッグ&ドロップで相対評価。AIは配置を支援しますが、判断するのはあなたです。',
                gradient: 'linear-gradient(135deg, #10b981, #14b8a6)',
                image: '/images/features/visual-comparison.png'
              },
              {
                icon: FileText,
                title: 'あなたの評価を言語化',
                description: 'ルーブリックでAIが評価理由の言語化を支援。説明責任を背負うあなたを支えます。',
                gradient: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                image: '/images/features/rubric-generation.png'
              }
            ].map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={solutionVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{ textAlign: 'center' }}
              >
                <div style={{
                  width: '5rem',
                  height: '5rem',
                  margin: '0 auto 1.5rem',
                  borderRadius: '1rem',
                  background: solution.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <solution.icon style={{ width: '2.5rem', height: '2.5rem' }} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                  {solution.title}
                </h3>
                <p style={{ color: '#666' }}>{solution.description}</p>
              </motion.div>
            ))}
          </div>

          {/* 活用イメージ */}
          <div style={{ marginTop: '4rem' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={solutionVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', marginBottom: '3rem' }}
            >
              <h3 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
                実際の活用イメージ
              </h3>
              <p style={{ fontSize: '1.25rem', color: '#666' }}>
                Evaloid導入時のイメージを具体的にご紹介します
              </p>
            </motion.div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4rem' }}>
              {[
                {
                  name: '田中先生',
                  role: '小学5年担任',
                  avatar: '👩‍🏫',
                  situation: '毎学期末の通知表作成で、30人分の評価コメントを書くのに週末を丸々使っていた。',
                  usage: [
                    'Evaloidで各生徒の学習データを管理し、AIが観点別の評価案を提示',
                    '提示された評価案を参考に、各生徒の特徴を活かしたコメントを効率的に作成',
                    '保護者面談では、可視化された評価データを見せながら具体的に説明できるように'
                  ],
                  result: '評価作成時間が大幅に短縮。家族との時間も確保できるようになった。',
                  benefits: [
                    '時間に余裕ができた',
                    '評価の根拠が明確に'
                  ],
                  color: '#059669',
                  bgColor: '#f0fdf4'
                },
                {
                  name: '佐藤先生',
                  role: '中学英語教諭',
                  avatar: '👨‍🏫',
                  situation: 'スピーキングテストの評価で、生徒間の公平性を保つことに不安を感じていた。',
                  usage: [
                    '評価ルーブリックをEvaloidで作成し、明確な評価基準を設定',
                    'テスト実施時は、AIが提案する評価ポイントを参考にしながら採点',
                    '生徒や保護者への説明時も、ルーブリックを見せて納得感のある説明を実現'
                  ],
                  result: '「なぜこの評価なのか」を明確に説明でき、生徒からの信頼も向上。',
                  benefits: [
                    '評価に自信が持てる',
                    '説明責任を果たせる'
                  ],
                  color: 'var(--color-info-dark)',
                  bgColor: '#eff6ff'
                }
              ].map((example, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={solutionVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.2 + index * 0.15 }}
                  style={{
                    position: 'relative',
                    backgroundColor: '#ffffff',
                    borderRadius: '1.5rem',
                    overflow: 'hidden',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                  }}
                >

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: windowWidth >= 768 ? '280px 1fr' : '1fr',
                    height: '100%'
                  }}>
                    {/* 左側：プロフィール部分 */}
                    <div style={{
                      backgroundColor: example.bgColor,
                      padding: '3rem 2rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRight: windowWidth >= 768 ? '1px solid #e5e7eb' : 'none',
                      borderBottom: windowWidth < 768 ? '1px solid #e5e7eb' : 'none'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '3rem',
                        marginBottom: '1rem',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}>
                        {example.avatar}
                      </div>
                      <h4 style={{ 
                        fontSize: '1.125rem', 
                        fontWeight: 'bold', 
                        color: '#111827', 
                        marginBottom: '0.25rem' 
                      }}>
                        {example.name}
                      </h4>
                      <p style={{ 
                        color: '#666', 
                        fontSize: '0.875rem',
                        marginBottom: '1.5rem'
                      }}>
                        {example.role}
                      </p>
                      
                      {/* メリットタグ */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                        {example.benefits.map((benefit, i) => (
                          <div key={i} style={{ 
                            backgroundColor: 'white',
                            padding: '0.5rem 1rem',
                            borderRadius: '2rem',
                            fontSize: '0.75rem',
                            color: example.color,
                            fontWeight: '500',
                            textAlign: 'center',
                            border: `1px solid ${example.color}33`
                          }}>
                            {benefit}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* 右側：ストーリー部分 */}
                    <div style={{ padding: '3rem' }}>
                      {/* Before: 課題 */}
                      <div style={{ marginBottom: '2rem' }}>
                        <div style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '2rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          marginBottom: '1rem'
                        }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#dc2626',
                            borderRadius: '50%'
                          }} />
                          Before
                        </div>
                        <p style={{ 
                          color: '#374151',
                          fontSize: '1rem',
                          lineHeight: '1.6',
                          margin: 0
                        }}>
                          {example.situation}
                        </p>
                      </div>

                      {/* Process: 活用方法 */}
                      <div style={{ marginBottom: '2rem' }}>
                        <div style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          backgroundColor: '#fef3c7',
                          color: '#f59e0b',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '2rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          marginBottom: '1rem'
                        }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: '#f59e0b',
                            borderRadius: '50%'
                          }} />
                          Process
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {example.usage.map((item, i) => (
                            <div key={i} style={{ 
                              display: 'flex',
                              gap: '0.75rem',
                              alignItems: 'start'
                            }}>
                              <div style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: example.bgColor,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginTop: '2px'
                              }}>
                                <span style={{ 
                                  color: example.color,
                                  fontSize: '0.75rem',
                                  fontWeight: '600'
                                }}>
                                  {i + 1}
                                </span>
                              </div>
                              <p style={{ 
                                color: '#666', 
                                fontSize: '0.875rem',
                                lineHeight: '1.5',
                                margin: 0
                              }}>
                                {item}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* After: 結果 */}
                      <div>
                        <div style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          backgroundColor: example.bgColor,
                          color: example.color,
                          padding: '0.25rem 0.75rem',
                          borderRadius: '2rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          marginBottom: '1rem'
                        }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: example.color,
                            borderRadius: '50%'
                          }} />
                          After
                        </div>
                        <div style={{ 
                          backgroundColor: '#f9fafb',
                          borderRadius: '0.75rem',
                          padding: '1.25rem'
                        }}>
                          <p style={{ 
                            color: '#111827',
                            fontWeight: '600',
                            fontSize: '0.9375rem',
                            margin: 0,
                            lineHeight: '1.5'
                          }}>
                            {example.result}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Detail Section */}
      <section ref={featuresRef} className="section-padding" style={{ backgroundColor: 'white' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={featuresVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              評価プロセスを支援する4つの機能
            </h2>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: Lightbulb,
                title: 'AI評価観点提案',
                description: '評価方針から最適な観点を提案。採用するかはあなたが決定。',
                gradient: 'linear-gradient(135deg, #fbbf24, #f97316)'
              },
              {
                icon: BarChart3,
                title: '比較評価システム',
                description: '数直線上で視覚的に評価。AIは配置を支援、最終判断はあなた。',
                gradient: 'linear-gradient(135deg, #60a5fa, #6366f1)'
              },
              {
                icon: FileText,
                title: 'ルーブリック作成支援',
                description: 'あなたの評価基準を言語化。AIが文章化を支援。',
                gradient: 'linear-gradient(135deg, #34d399, #10b981)'
              },
              {
                icon: Award,
                title: '成績表作成',
                description: 'PDFレポートを生成。個別フィードバックもあなたの言葉で。',
                gradient: 'linear-gradient(135deg, #a78bfa, #ec4899)'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={featuresVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  transition: 'box-shadow 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div style={{
                  width: '4rem',
                  height: '4rem',
                  marginBottom: '1.5rem',
                  borderRadius: '1rem',
                  background: feature.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <feature.icon style={{ width: '2rem', height: '2rem' }} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.75rem' }}>
                  {feature.title}
                </h3>
                <p style={{ color: '#666' }}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section ref={screenshotsRef} className="section-padding" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={screenshotsVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
              シンプルで使いやすい画面設計
            </h2>
            <p style={{ fontSize: '1.25rem', color: '#666' }}>
              直感的な操作で、誰でもすぐに使い始められます
            </p>
          </motion.div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: windowWidth >= 768 ? 'repeat(2, 1fr)' : '1fr',
            gap: '2rem' 
          }}>
            {[
              {
                image: '/images/screenshots/education-dashboard.png',
                caption: 'ダッシュボード - 複数のプロジェクトを一覧表示し、進捗状況や評価完了率を一目で確認。新規プロジェクト作成から既存プロジェクトの継続作業まで、すべてここから開始できます。'
              },
              {
                image: '/images/screenshots/education-evaluation.png',
                caption: '比較評価 - 製品を数直線上にドラッグ&ドロップで配置するだけの直感的操作。各評価観点で製品同士を比較しながら、相対的な位置関係を視覚的に決定できます。'
              },
              {
                image: '/images/screenshots/education-rubric.png',
                caption: 'ルーブリック生成 - あなたの評価結果から自動でルーブリック表を作成。各等級の判定基準を明文化し、評価の透明性と一貫性を確保します。内容は自由に編集可能です。'
              },
              {
                image: '/images/screenshots/education-report.png',
                caption: '成績表出力 - 個別の評価結果をPDF形式で美しく整形。学習者ごとの詳細なフィードバックコメントも含めて、プロフェッショナルな成績表を自動生成します。'
              }
            ].map((screenshot, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={screenshotsVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '1rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  transition: 'box-shadow 0.3s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div style={{
                  width: '100%',
                  height: '20rem',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <img
                    src={screenshot.image}
                    alt={screenshot.caption.split(' - ')[0]}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'top center',
                      transform: 'scale(1.2)',
                      transformOrigin: 'center top'
                    }}
                  />
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{ 
                    color: '#111827', 
                    fontWeight: '600', 
                    fontSize: '1.125rem',
                    marginBottom: '0.75rem',
                    margin: '0 0 0.75rem 0'
                  }}>
                    {screenshot.caption.split(' - ')[0]}
                  </h3>
                  <p style={{ 
                    color: '#6b7280', 
                    fontSize: '0.9375rem',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {screenshot.caption.split(' - ')[1]}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        position: 'relative',
        padding: '6rem 0',
        background: 'linear-gradient(135deg, #047857 0%, #059669 50%, #14b8a6 100%)',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <div style={{
            position: 'absolute',
            top: '2.5rem',
            right: '2.5rem',
            width: '18rem',
            height: '18rem',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            filter: 'blur(48px)'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '2.5rem',
            left: '2.5rem',
            width: '24rem',
            height: '24rem',
            backgroundColor: 'rgba(45, 212, 191, 0.2)',
            borderRadius: '50%',
            filter: 'blur(48px)'
          }} />
        </div>

        <div className="container-custom" style={{ position: 'relative', zIndex: 10 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1.5rem' }}>
              評価業務を、もっとシンプルに。
            </h2>
            <p style={{ fontSize: '1.25rem', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '2rem', maxWidth: '42rem', margin: '0 auto 2rem' }}>
              AIの支援を受けながら、あなたらしい公正な評価を実現しませんか？
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => navigate('/auth')}
                  style={{
                    backgroundColor: 'white',
                    color: '#047857',
                    fontWeight: 'bold',
                    padding: '1rem 2rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  無料で始める
                </button>
                <button
                  onClick={() => setIsConsultationModalOpen(true)}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(8px)',
                    color: 'white',
                    fontWeight: '600',
                    padding: '1rem 2rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                  }}
                >
                  導入相談をする
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', color: 'rgba(255, 255, 255, 0.8)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle style={{ width: '1.25rem', height: '1.25rem' }} />
                <span>無料でお試し可能（3プロジェクトまで）</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle style={{ width: '1.25rem', height: '1.25rem' }} />
                <span>導入サポート充実（オンライン研修付き）</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle style={{ width: '1.25rem', height: '1.25rem' }} />
                <span>データは完全暗号化で安心</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: '#111827', color: 'white', padding: '3rem 0' }}>
        <div className="container-custom">
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#9ca3af' }}>
              © 2024 Evaloid. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <TrialSignupModal
        isOpen={isTrialModalOpen}
        onClose={() => setIsTrialModalOpen(false)}
      />
      <ConsultationModal
        isOpen={isConsultationModalOpen}
        onClose={() => setIsConsultationModalOpen(false)}
      />
      <DemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
      />
    </div>
  )
}

export default EducationLP